import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { cn } from 'helpers/cn';
import { useSurveyTimeline } from '@/state/surveys/useSurveyTimeline';
import { seriesColor } from '../../Experiments/Details/ExperimentStatistics/control-options';
import {
  AXIS_TICK,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from '../../Experiments/Details/ExperimentStatistics/StatisticsChart';

type ResponsesOverTimeProps = {
  surveyId: number;
  /** The score's caption on instrument surveys; absent on Custom, which has no single score. */
  scoreCaption?: string;
};

const COLOR = seriesColor(0);
const PILL =
  'micro-label rounded-pill flex min-h-9 items-center border px-2.5 py-1 transition-colors duration-150 md:min-h-0';

/**
 * Responses, and the score, per day or week.
 *
 * Two charts rather than one with two y-axes: a count and a score on different
 * scales sharing a plot invite reading a crossing of the lines as meaning
 * something. Each is a single series, so the heading names it and there is no
 * legend. Furnished like the statistics screen's charts, whose axis and tooltip
 * styles are imported rather than copied.
 */
export const ResponsesOverTime = ({ surveyId, scoreCaption }: ResponsesOverTimeProps) => {
  // Its own data and interval, so switching between days and weeks re-renders this
  // panel alone.
  const { points, interval, setTimelineInterval: onIntervalChange } = useSurveyTimeline(surveyId);
  const data = points.map((point) => ({
    date: point.date,
    Responses: point.responses,
    Score: point.score ?? undefined,
  }));
  const hasScore = !!scoreCaption && points.some((point) => point.score !== null);

  return (
    <section className="bg-panel border-line rounded-panel border" data-tour="survey-results-timeline">
      <div className="border-line flex flex-col gap-2 border-b px-4 py-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-foreground m-0 text-body font-medium">Over time</h2>
        <div className="flex items-center gap-1.5">
          {(['day', 'week'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onIntervalChange(value)}
              aria-pressed={interval === value}
              className={cn(
                PILL,
                interval === value
                  ? 'bg-sel-bg border-sel-line text-foreground'
                  : 'border-input text-dim hover:bg-hover hover:text-foreground',
              )}
            >
              {value === 'day' ? 'By day' : 'By week'}
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 p-3">
        <div className="bg-panel2 border-line rounded-card border p-3">
          <p className="micro-label m-0">Responses per {interval}</p>
          <div className="mt-2 h-[200px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={AXIS_TICK}
                  tickMargin={8}
                  minTickGap={24}
                  stroke="var(--line)"
                  tickLine={false}
                />
                <YAxis
                  width={40}
                  tick={AXIS_TICK}
                  stroke="var(--line)"
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  cursor={{ fill: 'hsl(var(--hover))' }}
                  contentStyle={TOOLTIP_CONTENT_STYLE}
                  labelStyle={TOOLTIP_LABEL_STYLE}
                  itemStyle={TOOLTIP_ITEM_STYLE}
                />
                <Bar dataKey="Responses" fill={COLOR} fillOpacity={0.85} maxBarSize={24} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {hasScore && (
          <div className="bg-panel2 border-line rounded-card border p-3">
            <p className="micro-label m-0">{scoreCaption}</p>
            <div className="mt-2 h-[160px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tick={AXIS_TICK}
                    tickMargin={8}
                    minTickGap={24}
                    stroke="var(--line)"
                    tickLine={false}
                  />
                  <YAxis width={40} tick={AXIS_TICK} stroke="var(--line)" tickLine={false} axisLine={false} />
                  <Tooltip
                    contentStyle={TOOLTIP_CONTENT_STYLE}
                    labelStyle={TOOLTIP_LABEL_STYLE}
                    itemStyle={TOOLTIP_ITEM_STYLE}
                  />
                  {/* connectNulls: a day with no scored answer has no score, not a score of zero. */}
                  <Line
                    type="monotone"
                    dataKey="Score"
                    stroke={COLOR}
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                    activeDot={{ r: 4, stroke: COLOR, strokeWidth: 2, fill: 'hsl(var(--panel))' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
